import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    useGetKostnadskjemaQuery,
    useCreateKostnadskjemaMutation,
    useGetKostnadstypeQuery,
    useGetTilleggssatserQuery,
    useGetKostkategoriQuery,
    useDeleteKostnadstypeMutation,
    useRestoreKostnadstypeMutation,
    useUpsertKostnadstypeMutation,
} from '@klp/prosjekt/services/rtkApi';
import type { KostnadsKalkyleProps } from './KostnadsKalkyleProps';
import { useFormikContext } from 'formik';
import { findCamundaPropertyValue } from '@klp/formgenerator/utils';
import { safelyParseFloat } from '@klp/prosjekt/utils';
import { getInitialValues } from './useKostnadKalkyleDefaults';
import { parseMvapliktig } from '../../Lonnsomhet/mvaDelsum';
import { parseDisabledTabs } from './disabledTabs';
import type { KostnadsKalkyleTab } from './disabledTabs';

export const useKostnadsKalkyleController = ({
    field,
    prosjektId,
    prosessInstansId,
    isReadOnly,
    isRequired,
    prosjektNavn,
}: KostnadsKalkyleProps) => {
    const { setFieldValue, values } =
        useFormikContext<Record<string, unknown>>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { id: fieldId, label: fieldLabel } = field;
    const [totalKostnadFormValue, setTotalKostnadFormValue] = useState(
        safelyParseFloat(field.value) || 0,
    );
    const disabledTabs = useMemo(
        () =>
            parseDisabledTabs(
                findCamundaPropertyValue('disabledTabs', field.properties),
            ),
        [field.properties],
    );
    const [selectedTab, setSelectedTabState] =
        useState<KostnadsKalkyleTab>('kostnadskalkyle');
    const setSelectedTab = useCallback(
        (tab: KostnadsKalkyleTab) => {
            if (!disabledTabs.has(tab)) {
                setSelectedTabState(tab);
            }
        },
        [disabledTabs],
    );

    const kalkyleShouldUpdateField = findCamundaPropertyValue(
        'shouldUpdateField',
        field.properties,
    );

    const { data: kostnadskjema, isLoading: isKostnadSkjemaLoading } =
        useGetKostnadskjemaQuery(
            {
                prosessInstansId,
            },
            {
                skip: !isModalOpen,
                refetchOnMountOrArgChange: true,
            },
        );

    const toggleModal = (open?: boolean) => {
        typeof open === 'boolean'
            ? setIsModalOpen(open)
            : setIsModalOpen((prev) => !prev);
    };
    const {
        data: kostnadstyper,
        isLoading: kostnadstyperLoading,
        refetch: refetchKostnadstyper,
    } = useGetKostnadstypeQuery(undefined, {
        skip: !isModalOpen,
    });

    const { data: kostkategorier, isLoading: kostkategorierLoading } =
        useGetKostkategoriQuery(undefined, {
            skip: !isModalOpen,
        });

    // Default-satsene bor i backend-koden, slik at de kun er definert ett sted.
    const { data: tilleggssatser, isLoading: tilleggssatserLoading } =
        useGetTilleggssatserQuery(undefined, {
            skip: !isModalOpen,
        });

    const [createKostnadskjema] = useCreateKostnadskjemaMutation();
    const [upsertKostnadstype] = useUpsertKostnadstypeMutation();
    const [deleteKostnadstype] = useDeleteKostnadstypeMutation();
    const [restoreKostnadstype] = useRestoreKostnadstypeMutation();

    useEffect(() => {
        if (!isModalOpen || isReadOnly || isKostnadSkjemaLoading) return;

        const valueString = totalKostnadFormValue.toString();
        if (kalkyleShouldUpdateField) {
            setFieldValue(kalkyleShouldUpdateField, valueString);
        }
        setFieldValue(field.id, valueString);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [totalKostnadFormValue]);

    const prosjektarealFromSchema = useMemo(() => {
        return values['prosjektareal'] as string | null;
    }, [values]);

    const leieobjektArealFromSchema = useMemo(() => {
        return values['utleieareal'] as string | null;
    }, [values]);

    const mvapliktig = useMemo(() => {
        return parseMvapliktig(values['mvapliktig']);
    }, [values]);

    const initialValues = useMemo(() => {
        // Vent på satsene før skjemaet initialiseres. Uten dette rakk
        // kostnadstyper å lande først, skjemaet ble bygget med satsene udefinert
        // og falt tilbake til 0 — som autosave så skrev til basen som om
        // brukeren hadde tastet 0. isLoading er kun true ved første henting, så
        // en feilende forespørsel låser ikke modalen; da degraderer vi til 0.
        if (!kostnadstyper || tilleggssatserLoading) return null;

        return getInitialValues(
            kostnadskjema,
            prosjektId,
            prosessInstansId,
            kostnadstyper,
            prosjektarealFromSchema,
            leieobjektArealFromSchema,
            // Completed schemas must stay exactly as persisted — never reconcile them.
            isReadOnly ? null : mvapliktig,
            tilleggssatser,
        );
    }, [
        kostnadskjema,
        kostnadstyper,
        prosjektId,
        prosessInstansId,
        prosjektarealFromSchema,
        leieobjektArealFromSchema,
        isReadOnly,
        mvapliktig,
        tilleggssatser,
        tilleggssatserLoading,
    ]);

    const shouldInvalidateVurderingsskjema = true;

    return {
        prosjektId,
        prosessInstansId,
        isReadOnly,
        isRequired,
        prosjektNavn,
        kostnadskjema,
        kostnadstyper,
        kostkategorier,
        isKostnadSkjemaLoading,
        kostnadstyperLoading,
        kostkategorierLoading,
        createKostnadskjema,
        shouldInvalidateVurderingsskjema,
        upsertKostnadstype,
        deleteKostnadstype,
        restoreKostnadstype,
        refetchKostnadstyper,
        isModalOpen,
        toggleModal,
        totalKostnadFormValue,
        setTotalKostnadFormValue,
        fieldId,
        fieldLabel,
        selectedTab,
        setSelectedTab,
        disabledTabs,
        initialValues,
    };
};
